import React from 'react';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import { GamifyIcon } from '../../../components/GamifyIcon';

const PaginationButton = styled.button<{
  isActive?: boolean;
  isArrow?: boolean;
}>`
  background-color: ${({ isActive }) => (isActive ? '#ff6a00' : '#fff')};
  color: ${({ isActive }) => (isActive ? '#fff' : '#ff6a00')};
  border: 1px solid #ff6a00;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ isActive }) => (isActive ? '#ff6a00' : '#fff3e6')};
  }

  &:disabled {
    background-color: #f0f0f3;
    border-color: #d9d9d9;
    color: #a0a0a0;
    cursor: not-allowed;

    svg {
      fill: #a0a0a0;
    }
  }

  svg {
    width: 20px;
    height: 20px;
    fill: ${({ isActive }) => (isActive ? '#fff' : '#ff6a00')};
  }
`;

const Ellipsis = styled.span`
  color: #ff6a00;
  font-weight: 600;
  padding: 0 8px;
`;

interface LeaderboardPaginationProps {
  nextTokensList: string[];
  currentToken: string;
  onPageChange: (nextToken: string) => void;
}

const LeaderboardPagination: React.FC<LeaderboardPaginationProps> = ({
  nextTokensList,
  currentToken,
  onPageChange,
}) => {
  const totalPages = nextTokensList.length + 1;
  const currentPageIndex = ['', ...nextTokensList].indexOf(currentToken);
  const currentPage = currentPageIndex + 1;

  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    const sidePages = 1;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    // Calculate start and end of visible pages
    let start = Math.max(2, currentPage - sidePages);
    let end = Math.min(totalPages - 1, currentPage + sidePages);

    // Adjust if we're near the start
    if (currentPage <= sidePages + 2) {
      end = Math.min(totalPages - 1, maxVisiblePages - 1);
    }

    // Adjust if we're near the end
    if (currentPage >= totalPages - sidePages - 1) {
      start = Math.max(2, totalPages - maxVisiblePages + 1);
    }

    // Add ellipsis and pages
    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (page: number | string) => {
    if (typeof page === 'number') {
      const newIndex = page - 1;
      const newToken = ['', ...nextTokensList][newIndex];
      onPageChange(newToken);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newIndex = currentPageIndex - 1;
      const newToken = ['', ...nextTokensList][newIndex];
      onPageChange(newToken);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newIndex = currentPageIndex + 1;
      const newToken = ['', ...nextTokensList][newIndex];
      onPageChange(newToken);
    }
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 20,
        maxWidth: '100%',
        overflowX: 'auto',
        padding: 10,
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
        }}
        onClick={handlePrevPage}
      >
        <GamifyIcon
          icon="leftArrow"
          color={currentPage === 1 ? '#a0a0a0' : '#ff6a00'}
          //   disabled={currentPage === 1}
        />
      </Box>

      {getVisiblePages().map((page, index) => {
        if (page === '...') {
          return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
        }

        return (
          <PaginationButton
            key={page}
            isActive={page === currentPage}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </PaginationButton>
        );
      })}
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
        }}
        onClick={handleNextPage}
        // disabled={currentPage === totalPages}
      >
        <GamifyIcon
          icon="rightArrow"
          color={currentPage === totalPages ? '#a0a0a0' : '#ff6a00'}
        />
      </Box>
    </Box>
  );
};

export default LeaderboardPagination;
